/**
 * 시약·폐기통·환경센서·KPI 목 데이터.
 * 출처: 와이어프레임 알람 화면 KPI 4칸(2350행) · `SAMPLE_RECIPE_JSON` 시약 8종(2031행).
 */
import type { EnvSensorSnapshot, ReagentLot, ResourceKpi, WasteContainer } from '@ctcells/contract'

/**
 * 시약 lot 목 — **서버 응답 모양 그대로다**(2026-09-20 `GET /reagents/lots` 실물 4건으로 고쳤다).
 *
 * 🔴 **빈 칸을 `null` 로 «채우지» 마라. 키를 «뺀다».** 서버가 `model_dump(exclude_none=True)` 라
 *    값 없는 칸을 실지 않는다 — 실물 한 건은 두 칸뿐이었다(`{"lot":…,"name":…,"version":0}`).
 *    종전 목은 다섯 건 모두 여섯 키를 채우고 `max_open_hours: null` 을 쓰고 있었다.
 *    ⇒ **목이 실물보다 순하면 `x !== null` 류 버그가 데모에서 영원히 안 보인다** —
 *    보정(`valid_until`)에서 실제로 그렇게 숨었다. 되돌리지 마라.
 * ⚠ `version` 은 실물이 항상 준다(0 도 준다 — `exclude_none` 은 `None` 만 뺀다).
 */
export const REAGENT_LOTS: ReagentLot[] = [
  {
    // 전 열이 찬 건 — 실물의 `…-DAPI-001` 꼴
    lot: 'LOT-DPBS-2608',
    name: 'DPBS',
    expiry: '2027-02-28',
    opened_at: '2026-09-09T09:00:00+09:00',
    max_open_hours: 168,
    storage_cond: '상온',
    version: 1,
  },
  {
    lot: 'LOT-FA16-2607',
    name: '16% 포름알데히드',
    expiry: '2026-11-30',
    opened_at: '2026-09-09T09:05:00+09:00',
    max_open_hours: 24,
    storage_cond: '상온',
    version: 1,
  },
  {
    // ⚠ **`max_open_hours` 키가 없다** — 기준값 미결(OQ-114)이라 안 적힌 lot 이 실재한다
    lot: 'LOT-AB1-2609',
    name: '1차 항체',
    expiry: '2026-12-15',
    opened_at: '2026-09-11T08:40:00+09:00',
    storage_cond: '4 ℃',
    version: 1,
  },
  {
    // ⚠ **`opened_at` 키가 없다**(미개봉). `null` 이 아니라 부재다 — 실물 `…-FIX-003` 꼴
    lot: 'LOT-AB2-2609',
    name: '2차 항체',
    expiry: '2026-12-15',
    max_open_hours: 24,
    storage_cond: '4 ℃',
    version: 0,
  },
  {
    // 🔴 유효기한이 지났다 — 사전 점검 차단 사유가 된다
    lot: 'LOT-HOE-2604',
    name: 'Hoechst 33342',
    expiry: '2026-09-05',
    opened_at: '2026-08-20T10:00:00+09:00',
    max_open_hours: 72,
    storage_cond: '4 ℃',
    version: 1,
  },
  {
    /*
     * 🔴 **네 칸이 전부 빠진 건.** 실물 `fe-20260920-rg-r1-ETOH-004` 가 정확히 이 꼴이었다:
     *    `{"lot":"…-ETOH-004","name":"에탄올 70%","version":0}`.
     *    **이 행을 지우지 마라** — 이것이 빠지면 목이 다시 실물보다 순해진다.
     */
    lot: 'LOT-ETOH-2609',
    name: '에탄올 70%',
    version: 0,
  },
]

export const WASTE_CONTAINERS: WasteContainer[] = [
  {
    container_id: 'waste-cent-1',
    module_id: 'cent',
    hazard: 'biohazard',
    location: '원심분리기 하부',
    capacity: 5,
    capacity_unit: 'L',
    alarm_threshold_pct: 85,
  },
  {
    container_id: 'waste-stain-1',
    module_id: 'stain',
    hazard: 'biohazard',
    location: '염색기 하부',
    capacity: 10,
    capacity_unit: 'L',
    alarm_threshold_pct: 85,
  },
]

export const ENV_SENSORS: EnvSensorSnapshot[] = [
  {
    sensor_id: 'env-lab-1',
    location: '실험실',
    status: 'online',
    readings: { temp_c: 21.4, humidity_pct: 44.2, lux: 312 },
    excursion: false,
    stale: false,
    last_seen: '2026-09-11T14:20:00+09:00',
    version: 88,
  },
  {
    // 🔴 값이 늦다. 장치 생존(`status`)과 다른 축이다 — 살아 있는데 값이 낡았다
    sensor_id: 'env-stain-1',
    location: '염색기 시약 보관',
    status: 'online',
    readings: { temp_c: 4.1 },
    excursion: false,
    stale: true,
    last_seen: '2026-09-11T14:12:00+09:00',
    version: 71,
  },
]

/**
 * 상단 KPI 4칸(폐기통 · 팁 · 시약 여유 · 시약 보관 온도).
 * ⚠ 합성값이다 — 이 넷을 한 번에 주는 엔드포인트가 계약에 없다.
 */
export const RESOURCE_KPI: ResourceKpi = {
  waste_pct: 81,
  waste_threshold_pct: 85,
  tip_remaining: 288,
  tip_capacity: 350,
  reagent_headroom_sec: 33840,
  reagent_required_sec: 31320,
  reagent_storage_celsius: 4.1,
  reagent_storage_setpoint_celsius: 4.0,
}
